import { Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FlowTestsService } from "../flow-tests.service";
import { LogicTreeTest } from "../../handlers/logic-tree-tests-handler.service";

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set(["completed", "failed"]);

@Component({
  selector: "app-flow-test-detail",
  templateUrl: "./flow-test-detail.component.html",
  styleUrls: ["./flow-test-detail.component.scss"],
})
export class FlowTestDetailComponent implements OnInit, OnDestroy {
  test: LogicTreeTest | null = null;
  isLoading = true;
  pollTimer: any = null;
  errorMessage: string | null = null;
  traceSteps: any[] = [];
  expandedSteps = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private flowTests: FlowTestsService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get("id"));
    if (!id) {
      this.errorMessage = "Missing test id";
      this.isLoading = false;
      return;
    }
    this.fetch(id);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private fetch(id: number): void {
    this.flowTests.get(id).subscribe({
      next: (test) => {
        this.test = test;
        this.traceSteps = this.buildSessionTrace();
        this.isLoading = false;
        if (!this.isTerminal()) {
          this.startPolling(id);
        } else {
          this.stopPolling();
        }
      },
      error: (err) => {
        this.errorMessage =
          err?.error?.message || err?.message || "Failed to load test";
        this.isLoading = false;
        this.stopPolling();
      },
    });
  }

  private startPolling(id: number): void {
    if (this.pollTimer) return;
    // Run outside Angular to avoid change detection churn; results re-enter
    // the zone via subscribe().
    this.zone.runOutsideAngular(() => {
      this.pollTimer = setInterval(() => {
        this.flowTests.get(id).subscribe({
          next: (test) => {
            this.zone.run(() => {
              this.test = test;
              this.traceSteps = this.buildSessionTrace();
              if (this.isTerminal()) this.stopPolling();
            });
          },
          error: () => {
            // Transient errors shouldn't stop polling unless we're done.
          },
        });
      }, POLL_INTERVAL_MS);
    });
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  isTerminal(): boolean {
    return !!this.test && TERMINAL_STATUSES.has(this.test.status);
  }

  resultSummary(): any {
    return this.test?.result || {};
  }

  private allCaptures(): any[] {
    const r = this.resultSummary();
    return r.captured_outbound || r.captured || [];
  }

  /**
   * Outbound captures exclude `ai_condition` entries — those are
   * flow decisions, not side effects, and render in Session Trace.
   */
  capturedOutbound(): any[] {
    return this.allCaptures().filter(
      (c) => (c?.provider || c?.extra?.provider) !== "ai_condition",
    );
  }

  captureSummary(cap: any): string {
    const data = cap?.extra?.data || {};
    const provider = cap?.provider || cap?.extra?.provider || "unknown";
    if (provider === "signal") {
      const headline =
        data.push_notification_headline || data.title || "(no headline)";
      const body =
        data.push_notification_text || data.push_notification_content || "";
      return `Push "${headline}" — ${body}`.trim();
    }
    if (provider === "email" || provider === "emailSend") {
      return `Email: ${data.subject || data.template || "(no subject)"}`;
    }
    if (provider === "webHook" || provider === "webhook") {
      return `Webhook: ${data.url || data.webhook_url || "(no url)"}`;
    }
    if (provider === "notification") {
      return `Notification: ${data.alert || data.message || "(no alert)"}`;
    }
    if (provider === "logicTree") {
      return `Scheduled re-enter flow (delay=${cap?.extra?.options?.delay ?? 0}ms)`;
    }
    return provider;
  }

  /**
   * Session Trace merges real history rows (edges the flow actually
   * traversed) with AI condition evaluations (decisions the flow made
   * at branching edges, including ones that resolved FALSE and
   * therefore never produced a history row). Sorted chronologically so
   * the reader sees the flow's reasoning and its traversal interleaved.
   *
   * Called from fetch/poll and cached in `traceSteps` so the template
   * binds against a stable array (not a new array per change-detection
   * tick), and so `expandedSteps` keys stay meaningful.
   */
  private buildSessionTrace(): any[] {
    const r = this.resultSummary();
    const steps: any[] = [];

    if (Array.isArray(r.sessions) && r.sessions.length) {
      r.sessions.forEach((session: any) => {
        (session.history || []).forEach((h: any, hi: number) => {
          steps.push({
            kind: "history",
            key: `h:${session.id}:${hi}:${h.created_at || ""}`,
            session_id: session.id,
            source: h.source,
            target: h.target,
            created_at: h.created_at,
            payload: h.payload,
          });
        });
      });
    } else if (Array.isArray(r.history || r.session_trace)) {
      (r.history || r.session_trace).forEach((h: any, hi: number) => {
        steps.push({
          kind: "history",
          key: `h:standalone:${hi}`,
          ...h,
        });
      });
    }

    this.allCaptures()
      .filter((c) => (c?.provider || c?.extra?.provider) === "ai_condition")
      .forEach((c, ci) => {
        const extra = c?.extra || {};
        steps.push({
          kind: "ai_eval",
          key: `ai:${ci}:${c.captured_at || ""}:${extra.edge_id || ""}`,
          session_id: c.logic_tree_session_id || null,
          source: extra.source || null,
          target: extra.target || null,
          created_at: c.captured_at,
          edge_name: extra.edge_name,
          edge_id: extra.edge_id,
          truthy: !!extra.truthy,
          summary: extra.summary || null,
          error: extra.error || null,
          prompt: extra.prompt || null,
          model: extra.model || null,
          api_used: extra.api_used || null,
          web_search_available: extra.web_search_available,
          web_search_calls: typeof extra.web_search_calls === "number" ? extra.web_search_calls : null,
          latency_ms: extra.latency_ms || null,
          raw: extra.raw || null,
        });
      });

    steps.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });

    return steps;
  }

  toggleStep(key: string): void {
    if (this.expandedSteps.has(key)) {
      this.expandedSteps.delete(key);
    } else {
      this.expandedSteps.add(key);
    }
  }

  isStepExpanded(key: string): boolean {
    return this.expandedSteps.has(key);
  }

  trackStep(_index: number, step: any): string {
    return step?.key || _index.toString();
  }

  sessions(): any[] {
    const r = this.resultSummary();
    return Array.isArray(r.sessions) ? r.sessions : [];
  }

  back(): void {
    this.router.navigate(["/flow-tests", "list"]);
  }

  rerun(): void {
    if (!this.test) return;
    this.flowTests
      .create(this.test.logic_tree_id, this.test.config || {})
      .subscribe({
        next: (newTest) => {
          this.router.navigate(["/flow-tests", newTest.id]);
        },
      });
  }

  remove(): void {
    if (!this.test) return;
    if (!confirm(`Delete flow test #${this.test.id}?`)) return;
    this.flowTests.remove(this.test.id).subscribe({
      next: () => this.router.navigate(["/flow-tests", "list"]),
    });
  }
}
